import { IntlShape } from 'react-intl'

import { AppDispatchNext, AppGetState } from 'actions/actions.typesNext'
import {
  calculateBannersUrlsAction,
  landingSearchAction,
} from 'actions/landing/landingAction'
import {
  fetchAdsLinksAction,
  fetchSeoSnippetAction,
  fetchTopLocationLinksAction,
  resolveSeoLocationAction,
} from 'actions/landing/seoAction'
import { setMetaDataAction } from 'actions/system/systemAction'
import { ApiResult } from 'api/fetchApi'
import { definitions } from 'api/generated/seo'
import { definitions as uniweb } from 'api/generated/uniweb'
import { SeoSearchPath } from 'api/landing/searchApi'
import { NodeHeaders } from 'api/NodeHeaders'
import {
  allCitiesPath,
  allRegionsPath,
  ANY,
  Gender,
  genderSearchParameters,
  TOP_LOCATION_LIMIT,
} from 'components/page/Landing/constants'
import {
  createLocationParams,
  findGenderCode,
  findPreferGenderCode,
  GenericGeoLandingParams,
  getLandingMetaDescription,
  getLandingTitle,
} from 'components/page/Landing/Landing.functions'
import { isSeriousRelaionship } from 'components/page/Landing/Special/Special.functions'
import { defaultSearchForm } from 'components/page/MainSearchSettings/constants'
import { removeUrlQuery } from 'functions/url'

import { fetchSnippetContentAction } from './fetchSnippetContentAction'

const isAllRegionsAndCities = (pathname: string) =>
  pathname.includes(allCitiesPath) || pathname.includes(allRegionsPath)

const findLimit = (regionsAndCities: boolean) => {
  if (regionsAndCities) {
    // TODO переделать после аутсорсеров: сейчас это означает без лимита
    // Используется для всех регионов и городов
    return 0
  }
  return TOP_LOCATION_LIMIT
}

const createParameters = (
  intl: IntlShape,
  locationType: string,
  gender: string,
  resolvedLocation: definitions['NamedLocation'] | undefined,
  partner: uniweb['PartnerShortInfo'] | null
) => {
  if (resolvedLocation) {
    return {
      intl,
      locationType,
      gender,
      values: {
        cityName: resolvedLocation.cityName,
        regionName: resolvedLocation.regionName,
        countryName: resolvedLocation.countryName,
        siteName: partner?.partnerSiteName.domainName,
      },
    }
  }
}

/**
 * Аутсорсеры свалили почти все данные в одну кучу.
 * Нужно будет в дальнейшем посмотреть, так ли это нужно.
 */
export const geoLandingDataAction =
  (params: GenericGeoLandingParams, intl: IntlShape, headers?: NodeHeaders) =>
  async (dispatch: AppDispatchNext, getState: AppGetState) => {
    const {
      router: {
        location: { pathname: rawPathname },
      },
      systemReducer: { partner },
      metaData,
    } = getState()
    const { locationId, locationType } = createLocationParams(params)
    const pathname = removeUrlQuery(rawPathname)

    dispatch(calculateBannersUrlsAction())

    if (locationId) {
      const regionsAndCities = isAllRegionsAndCities(pathname)

      const genderParameter: Gender | undefined = params.gender
        ? genderSearchParameters[params.gender]
        : undefined

      const promises: Promise<unknown>[] = [
        dispatch(
          resolveSeoLocationAction(locationId, locationType, undefined, headers)
        ),
        dispatch(
          fetchTopLocationLinksAction(
            locationId,
            locationType,
            ANY,
            findLimit(regionsAndCities),
            headers
          )
        ),
      ]

      if (!regionsAndCities) {
        const searchRequest = {
          seoLocationId: {
            locationId: Number(locationId),
            type: locationType,
          },
          request: {
            ...defaultSearchForm,
            withPhoto: true,
            limit: 16, // https://redmine.mamba.ru/issues/115872#note-28
            lookFor: findGenderCode(params.gender),
            lookForPrefGender: findPreferGenderCode(params.gender),
          },
        } as unknown as SeoSearchPath['post']['parameters']['body']['body']

        /**
         * https://youtrack.mamba.ru/issue/M-10141#focus=Comments-4-65317.0-0
         */
        if (isSeriousRelaionship(pathname)) {
          /** Бекенд должен поправить доку */
          ;(
            searchRequest as {
              source: { value: 'common' | 'rating' }
            }
          ).source = { value: 'common' }
          searchRequest.request!.target = ['SeriousRelationship']
        }

        promises.push(dispatch(landingSearchAction(searchRequest, headers)))

        promises.push(
          dispatch(
            fetchAdsLinksAction(locationId, locationType, undefined, headers)
          )
        )

        promises.push(
          dispatch(
            fetchSeoSnippetAction(
              locationId,
              locationType,
              genderParameter,
              headers
            )
          )
        )
        promises.push(dispatch(fetchSnippetContentAction(pathname)))
      }

      const [seoLocation] = await Promise.all(promises)

      const { result } = seoLocation as ApiResult
      const { location: resolvedLocation } =
        result as definitions['SeoLocationDto']

      const parameters = createParameters(
        intl,
        locationType,
        params.gender as string,
        resolvedLocation,
        partner
      )

      /**
       * Установка title для landing. Если metaData(customMeta -> api/bootstrap) загружены, то не надо ставить.
       */
      if (!metaData.hasInfo && parameters) {
        const title = getLandingTitle(parameters)
        const description = getLandingMetaDescription(parameters)
        dispatch(
          setMetaDataAction({
            title,
            description,
            keywords: '',
          })
        )
      }
    }
  }
